import { formatInTimeZone, fromZonedTime } from "date-fns-tz";
import { resolveSchedulerVotingDeadline } from "../../../lib/voting-deadlines";

const LOCAL_INPUT_FORMAT = "yyyy-MM-dd'T'HH:mm";

export function toSessionDeadlineLocalValue(value, timeZone) {
  const deadline = resolveSchedulerVotingDeadline({ votingDeadlineAt: value });
  if (!deadline || !timeZone) return "";
  try {
    return formatInTimeZone(deadline, timeZone, LOCAL_INPUT_FORMAT);
  } catch {
    return "";
  }
}

export function parseSessionDeadlineLocalValue(value, timeZone) {
  if (!value) return null;
  if (!timeZone) return new Date(Number.NaN);
  try {
    const parsed = fromZonedTime(value, timeZone);
    return Number.isFinite(parsed.getTime()) ? parsed : new Date(Number.NaN);
  } catch {
    return new Date(Number.NaN);
  }
}

export function validateSessionDeadlineChange({
  localValue,
  timeZone,
  initialLocalValue = "",
  isEditing = false,
  now = Date.now(),
  slots = [],
}) {
  if (!localValue) {
    return { valid: true, deadlineAt: null, error: null };
  }

  const deadlineAt = parseSessionDeadlineLocalValue(localValue, timeZone);
  if (!Number.isFinite(deadlineAt?.getTime?.())) {
    return { valid: false, deadlineAt: null, error: "Voting deadline is invalid." };
  }

  const unchangedExistingValue =
    isEditing && Boolean(initialLocalValue) && localValue === initialLocalValue;
  if (deadlineAt.getTime() <= Number(now) && !unchangedExistingValue) {
    return {
      valid: false,
      deadlineAt: null,
      error: "Voting deadline must be in the future or cleared.",
    };
  }

  if (unchangedExistingValue) {
    return { valid: true, deadlineAt, error: null, warning: null };
  }

  const slotStarts = (slots || [])
    .map((slot) => {
      const start = slot?.start instanceof Date ? slot.start : new Date(slot?.start);
      return Number.isFinite(start.getTime()) ? start : null;
    })
    .filter(Boolean)
    .sort((left, right) => left.getTime() - right.getTime());
  if (slotStarts.length > 0) {
    const earliest = slotStarts[0];
    const final = slotStarts[slotStarts.length - 1];
    if (deadlineAt.getTime() >= final.getTime()) {
      return {
        valid: false,
        deadlineAt: null,
        error: "Voting deadline must be before the final proposed session time.",
        warning: null,
      };
    }
    if (deadlineAt.getTime() > earliest.getTime()) {
      return {
        valid: true,
        deadlineAt,
        error: null,
        warning: "Voting will remain open after the first proposed session time begins.",
      };
    }
  }

  return { valid: true, deadlineAt, error: null, warning: null };
}

export function buildSessionDeadlinePreset({
  slots = [],
  offsetMinutes,
  timeZone,
  now = Date.now(),
}) {
  const starts = (slots || [])
    .map((slot) => {
      const value = slot?.start instanceof Date ? slot.start : new Date(slot?.start);
      return Number.isFinite(value.getTime()) ? value.getTime() : null;
    })
    .filter(Number.isFinite);
  if (starts.length === 0 || !Number.isFinite(offsetMinutes)) return "";
  const deadline = new Date(Math.min(...starts) - offsetMinutes * 60_000);
  if (deadline.getTime() <= Number(now)) return "";
  return toSessionDeadlineLocalValue(deadline, timeZone);
}
